import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import { cva } from "class-variance-authority";

type AvatarVariant = "internal" | "external";
type AvatarSize = "md" | "xs";
type AvatarRootProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Root
>;
type AvatarBaseProps = Omit<
  AvatarRootProps,
  "aria-hidden" | "aria-label" | "children" | "className" | "role"
> & {
  initials: string;
  variant: AvatarVariant;
};
type AvatarProps =
  | (AvatarBaseProps & {
      "aria-label": string;
      size?: "md";
      src?: string;
    })
  | (AvatarBaseProps & {
      "aria-label"?: never;
      size: "xs";
      src?: string;
    });

const avatarVariants = cva(
  [
    "inline-flex",
    "shrink-0",
    "items-center",
    "justify-center",
    "overflow-hidden",
    "rounded-full",
    "font-(family-name:--font-family-label)",
    "font-(number:--font-weight-medium)",
    "select-none",
  ],
  {
    variants: {
      size: {
        md: [
          "size-(--spacing-40)",
          "text-(length:--font-size-title-inter-medium)",
          "leading-(--font-line-height-24)",
        ],
        xs: [
          "size-(--spacing-20)",
          "text-(length:--font-size-title-inter-xxsmall)",
          "leading-(--font-line-height-20)",
        ],
      },
      variant: {
        internal: [
          "bg-surface-inverse-surface",
          "text-surface-inverse-on-surface",
        ],
        external: ["bg-surface-container-highest", "text-surface-on-surface"],
      },
    },
  },
);

/**
 * Circular user identity mark shown as initials or a profile image.
 *
 * @example
 * <Avatar aria-label="Jane Doe" initials="JD" variant="internal" />
 * <Avatar aria-label="María García" initials="MG" variant="external" />
 * <Avatar initials="JD" size="xs" variant="internal" />
 */
function Avatar({
  initials,
  size = "md",
  variant,
  src,
  "aria-label": ariaLabel,
  ...props
}: AvatarProps) {
  const isHidden = size === "xs";
  const rootLabel = !src && !isHidden ? ariaLabel : undefined;
  const fallbackLabel = src && !isHidden ? ariaLabel : undefined;

  return (
    <AvatarPrimitive.Root
      {...props}
      data-slot="avatar"
      data-variant={variant}
      role={rootLabel ? "img" : undefined}
      aria-label={rootLabel}
      aria-hidden={isHidden || undefined}
      className={avatarVariants({ size, variant })}
    >
      {src ? (
        <AvatarPrimitive.Image
          data-slot="avatar-image"
          src={src}
          alt={isHidden ? "" : (ariaLabel ?? "")}
          className="h-full w-full object-cover"
        />
      ) : null}
      <AvatarPrimitive.Fallback
        data-slot="avatar-fallback"
        role={fallbackLabel ? "img" : undefined}
        aria-label={fallbackLabel}
      >
        <span aria-hidden="true">{initials}</span>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export { Avatar };
export type { AvatarProps, AvatarSize, AvatarVariant };
